import BaseService from '@/services/BaseService';
import { ResponseError } from '@/lib/exceptions';
import type { ChartDataPoint } from './ChartQueryService';

export type FundFinanceDashboardFilterOptions = {
  dealNames: string[];
};

export type FundFinanceTapeDashboardKpiLatest = {
  asOfDateIso: string;
  totalCalledCapital: number;
  totalUncalledCapital: number;
  totalAssetValue: number;
  waPercentCalled: number | null;
  activeInvestorCount: number;
  dealsInMonth: number;
  dealsWithZeroUncalled: string[];
  dealsWithZeroCalled: string[];
  dealsWithZeroUncalledAndZeroCalled: string[];
  dealsWithZeroAssetValue: string[];
};

export type FundFinanceDealMetricByMonthRow = {
  asOfMonth: string;
  dealName: string;
  amount: number;
};

export type FundFinanceDealCategorySliceRow = {
  dealName: string;
  category: string;
  value: number;
};

export type FundFinanceCategoryMetricByMonthRow = {
  asOfMonth: string;
  category: string;
  commitment: number;
  portfolioCommitmentTotal: number;
};

export type FundFinanceSponsorParentOverTime = {
  rows: FundFinanceCategoryMetricByMonthRow[];
  dealsWithSponsorInSelectedMonth: number;
  dealsInSelectedMonth: number;
};

export type FundFinanceGeographyConcentrationOverTime = {
  rows: FundFinanceCategoryMetricByMonthRow[];
  dealsWithGeographyInSelectedMonth: number;
  dealsInSelectedMonth: number;
  percentInvestorRowsWithoutGeography: number;
};

export type FundFinanceCreditQualityRow = {
  investorName: string;
  dealName: string;
  spRating: string;
  moodysRating: string | null;
  capitalCommitment: number;
  uncalledCapital: number;
  percentCalled: number | null;
};

export type FundFinanceDashboardFilters = {
  dealName: readonly string[];
  asOfDate: string;
};

const FILTER_OPTIONS_PATH = 'fund-finance-dashboard/filter-options';

function stringArrayFromBody(body: Record<string, unknown>, camelKey: string, pascalKey: string): string[] {
  const raw = body[camelKey] ?? body[pascalKey];
  if (!Array.isArray(raw)) {
    return [];
  }
  return raw.filter((x): x is string => typeof x === 'string');
}

function parseNum(v: unknown): number | null {
  if (v == null) {
    return null;
  }
  if (typeof v === 'number' && Number.isFinite(v)) {
    return v;
  }
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function appendDealNames(qs: URLSearchParams, dealNames: readonly string[]) {
  for (const d of dealNames) {
    const t = d.trim();
    if (t) {
      qs.append('dealName', t);
    }
  }
}

function appendAsOfDate(qs: URLSearchParams, asOfDate: string) {
  const t = asOfDate.trim();
  if (t) {
    qs.set('asOfDate', t);
  }
}

function parseKpiRow(raw: unknown): FundFinanceTapeDashboardKpiLatest | null {
  if (raw == null || typeof raw !== 'object') {
    return null;
  }
  const o = raw as Record<string, unknown>;
  const asOf = o.asOfDateIso ?? o.AsOfDateIso;
  if (typeof asOf !== 'string' || asOf.length === 0) {
    return null;
  }
  const totalCalledCapital = parseNum(o.totalCalledCapital ?? o.TotalCalledCapital);
  const totalUncalledCapital = parseNum(o.totalUncalledCapital ?? o.TotalUncalledCapital);
  const totalAssetValue = parseNum(o.totalAssetValue ?? o.TotalAssetValue);
  if (totalCalledCapital == null || totalUncalledCapital == null || totalAssetValue == null) {
    return null;
  }

  return {
    asOfDateIso: asOf,
    totalCalledCapital,
    totalUncalledCapital,
    totalAssetValue,
    waPercentCalled: parseNum(o.waPercentCalled ?? o.WaPercentCalled),
    activeInvestorCount: parseNum(o.activeInvestorCount ?? o.ActiveInvestorCount) ?? 0,
    dealsInMonth: parseNum(o.dealsInMonth ?? o.DealsInMonth) ?? 0,
    dealsWithZeroUncalled: stringArrayFromBody(o, 'dealsWithZeroUncalled', 'DealsWithZeroUncalled'),
    dealsWithZeroCalled: stringArrayFromBody(o, 'dealsWithZeroCalled', 'DealsWithZeroCalled'),
    dealsWithZeroUncalledAndZeroCalled: stringArrayFromBody(
      o,
      'dealsWithZeroUncalledAndZeroCalled',
      'DealsWithZeroUncalledAndZeroCalled',
    ),
    dealsWithZeroAssetValue: stringArrayFromBody(o, 'dealsWithZeroAssetValue', 'DealsWithZeroAssetValue'),
  };
}

function parseDealMetricByMonthRows(raw: unknown): FundFinanceDealMetricByMonthRow[] {
  if (raw == null || typeof raw !== 'object') {
    return [];
  }
  const o = raw as Record<string, unknown>;
  const list = o.rows ?? o.Rows;
  if (!Array.isArray(list)) {
    return [];
  }
  const out: FundFinanceDealMetricByMonthRow[] = [];
  for (const item of list) {
    if (item == null || typeof item !== 'object') {
      continue;
    }
    const r = item as Record<string, unknown>;
    const asOfMonth = r.asOfMonth ?? r.AsOfMonth;
    const dealName = r.dealName ?? r.DealName;
    const amount = parseNum(r.amount ?? r.Amount);
    if (typeof asOfMonth !== 'string' || asOfMonth.length === 0) {
      continue;
    }
    if (typeof dealName !== 'string' || dealName.length === 0) {
      continue;
    }
    if (amount == null) {
      continue;
    }
    out.push({ asOfMonth, dealName, amount });
  }
  return out;
}

function parseDealCategorySliceRows(raw: unknown): FundFinanceDealCategorySliceRow[] {
  if (raw == null || typeof raw !== 'object') {
    return [];
  }
  const o = raw as Record<string, unknown>;
  const list = o.rows ?? o.Slices ?? o.slices;
  if (!Array.isArray(list)) {
    return [];
  }
  const out: FundFinanceDealCategorySliceRow[] = [];
  for (const item of list) {
    if (item == null || typeof item !== 'object') {
      continue;
    }
    const r = item as Record<string, unknown>;
    const dealName = r.dealName ?? r.DealName;
    const category = r.category ?? r.Category;
    const value = parseNum(r.value ?? r.Value);
    if (typeof dealName !== 'string' || dealName.length === 0) {
      continue;
    }
    if (typeof category !== 'string' || category.length === 0) {
      continue;
    }
    if (value == null) {
      continue;
    }
    out.push({ dealName, category, value });
  }
  return out;
}

function parseCategoryMetricByMonthRows(raw: unknown): FundFinanceCategoryMetricByMonthRow[] {
  if (raw == null || typeof raw !== 'object') {
    return [];
  }
  const o = raw as Record<string, unknown>;
  const list = o.rows ?? o.Rows;
  if (!Array.isArray(list)) {
    return [];
  }
  const out: FundFinanceCategoryMetricByMonthRow[] = [];
  for (const item of list) {
    if (item == null || typeof item !== 'object') {
      continue;
    }
    const r = item as Record<string, unknown>;
    const asOfMonth = r.asOfMonth ?? r.AsOfMonth;
    const category = r.category ?? r.Category;
    const commitment = parseNum(r.commitment ?? r.Commitment);
    const portfolioCommitmentTotal = parseNum(r.portfolioCommitmentTotal ?? r.PortfolioCommitmentTotal);
    if (typeof asOfMonth !== 'string' || asOfMonth.length === 0) {
      continue;
    }
    if (typeof category !== 'string' || category.length === 0) {
      continue;
    }
    if (commitment == null || portfolioCommitmentTotal == null) {
      continue;
    }
    out.push({ asOfMonth, category, commitment, portfolioCommitmentTotal });
  }
  return out;
}

function parseSponsorParentOverTime(raw: unknown): FundFinanceSponsorParentOverTime {
  if (raw == null || typeof raw !== 'object') {
    return { rows: [], dealsWithSponsorInSelectedMonth: 0, dealsInSelectedMonth: 0 };
  }
  const o = raw as Record<string, unknown>;
  return {
    rows: parseCategoryMetricByMonthRows(raw),
    dealsWithSponsorInSelectedMonth:
      parseNum(o.dealsWithSponsorInSelectedMonth ?? o.DealsWithSponsorInSelectedMonth) ?? 0,
    dealsInSelectedMonth: parseNum(o.dealsInSelectedMonth ?? o.DealsInSelectedMonth) ?? 0,
  };
}

function parseGeographyConcentrationOverTime(raw: unknown): FundFinanceGeographyConcentrationOverTime {
  if (raw == null || typeof raw !== 'object') {
    return {
      rows: [],
      dealsWithGeographyInSelectedMonth: 0,
      dealsInSelectedMonth: 0,
      percentInvestorRowsWithoutGeography: 0,
    };
  }
  const o = raw as Record<string, unknown>;
  return {
    rows: parseCategoryMetricByMonthRows(raw),
    dealsWithGeographyInSelectedMonth:
      parseNum(o.dealsWithGeographyInSelectedMonth ?? o.DealsWithGeographyInSelectedMonth) ?? 0,
    dealsInSelectedMonth: parseNum(o.dealsInSelectedMonth ?? o.DealsInSelectedMonth) ?? 0,
    percentInvestorRowsWithoutGeography:
      parseNum(o.percentInvestorRowsWithoutGeography ?? o.PercentInvestorRowsWithoutGeography) ?? 0,
  };
}

/** Pivot API rows into AutoChart stacked-bar points (month × facility). */
export function fundFinanceDealMetricRowsToChartDataPoints(
  rows: readonly FundFinanceDealMetricByMonthRow[],
): ChartDataPoint[] {
  const totals = new Map<string, number>();
  for (const row of rows) {
    const key = `${row.asOfMonth}\u0001${row.dealName}`;
    totals.set(key, (totals.get(key) ?? 0) + row.amount);
  }

  const points: ChartDataPoint[] = [];
  for (const [key, value] of totals) {
    const sep = key.indexOf('\u0001');
    points.push({
      xAxis: key.slice(0, sep),
      series: key.slice(sep + 1),
      value,
    });
  }
  return points;
}

/** Pivot snapshot rows into stacked-bar points (facility × sub-asset class). */
export function fundFinanceDealCategorySliceRowsToChartDataPoints(
  rows: readonly FundFinanceDealCategorySliceRow[],
): ChartDataPoint[] {
  return rows.map(row => ({
    xAxis: row.dealName,
    series: row.category,
    value: row.value,
  }));
}

/** Pivot category-over-time rows into stacked-bar points (month × category). */
export function fundFinanceCategoryMetricRowsToChartDataPoints(
  rows: readonly FundFinanceCategoryMetricByMonthRow[],
): ChartDataPoint[] {
  return rows.map(row => ({
    xAxis: row.asOfMonth,
    series: row.category,
    value: row.commitment,
    companionValue: row.portfolioCommitmentTotal,
  }));
}

/**
 * Commitment portfolio share (0–1) per month for percent-line charts.
 * Only emits months with positive commitment so missing months stay null in the pivot
 * (AG Charts gaps the line when <c>connectMissingData</c> is false).
 */
export function fundFinanceCategoryMetricRowsToCommitmentPercentLineChartDataPoints(
  rows: readonly FundFinanceCategoryMetricByMonthRow[],
): ChartDataPoint[] {
  const out: ChartDataPoint[] = [];
  for (const row of rows) {
    if (!(row.commitment > 0) || !(row.portfolioCommitmentTotal > 0)) {
      continue;
    }
    out.push({
      xAxis: row.asOfMonth,
      series: row.category,
      value: row.commitment / row.portfolioCommitmentTotal,
      portfolioShareNumerator: row.commitment,
      portfolioShareDenominator: row.portfolioCommitmentTotal,
      companionValue: row.commitment,
    });
  }
  return out;
}

function parseCreditQualityRows(raw: unknown): FundFinanceCreditQualityRow[] {
  if (raw == null || typeof raw !== 'object') {
    return [];
  }
  const o = raw as Record<string, unknown>;
  const list = o.rows ?? o.Rows;
  if (!Array.isArray(list)) {
    return [];
  }
  const out: FundFinanceCreditQualityRow[] = [];
  for (const item of list) {
    if (item == null || typeof item !== 'object') {
      continue;
    }
    const r = item as Record<string, unknown>;
    const investorName = r.investorName ?? r.InvestorName;
    const dealName = r.dealName ?? r.DealName;
    const spRating = r.spRating ?? r.SpRating;
    const capitalCommitment = parseNum(r.capitalCommitment ?? r.CapitalCommitment);
    const uncalledCapital = parseNum(r.uncalledCapital ?? r.UncalledCapital);
    if (typeof investorName !== 'string' || investorName.length === 0) {
      continue;
    }
    if (typeof dealName !== 'string' || dealName.length === 0) {
      continue;
    }
    if (typeof spRating !== 'string' || spRating.length === 0) {
      continue;
    }
    if (capitalCommitment == null || uncalledCapital == null) {
      continue;
    }
    const moodysRaw = r.moodysRating ?? r.MoodysRating;
    const moodysRating = typeof moodysRaw === 'string' && moodysRaw.trim().length > 0 ? moodysRaw.trim() : null;
    out.push({
      investorName,
      dealName,
      spRating,
      moodysRating,
      capitalCommitment,
      uncalledCapital,
      percentCalled: parseNum(r.percentCalled ?? r.PercentCalled),
    });
  }
  return out;
}

export class FundFinanceDashboardService extends BaseService {
  constructor() {
    super('/api/digitalconnect-service');
  }

  getJsonBody = async (path: string) => {
    const raw = await this.get({ path: path });
    if (typeof Response !== 'undefined' && raw instanceof Response) {
      if (!raw.ok) {
        throw new ResponseError(`HTTP ${raw.status}`, raw.status);
      }
      return (await raw.json()) as Record<string, unknown>;
    }
    if (raw && typeof raw === 'object') {
      return raw as Record<string, unknown>;
    }
    return {};
  };

  fetchWithDealFilter = async (
    pathBase: string,
    filters: FundFinanceDashboardFilters,
  ): Promise<Record<string, unknown>> => {
    const qs = new URLSearchParams();
    appendAsOfDate(qs, filters.asOfDate);
    appendDealNames(qs, filters.dealName);
    const q = qs.toString();
    const path = q.length > 0 ? `${pathBase}?${q}` : pathBase;
    return this.getJsonBody(path);
  };

  fetchFundFinanceDashboardFilterOptions = async () => {
    const body = await this.getJsonBody(FILTER_OPTIONS_PATH);
    return {
      dealNames: stringArrayFromBody(body, 'dealNames', 'DealNames'),
    } satisfies FundFinanceDashboardFilterOptions;
  };

  fetchFundFinanceDashboardAsOfDates = async () => {
    const body = await this.getJsonBody('fund-finance-dashboard/as-of-dates');
    return stringArrayFromBody(body, 'asOfDates', 'AsOfDates');
  };

  fetchFundFinanceTapeDashboardKpi = async (filters: FundFinanceDashboardFilters) => {
    const body = await this.fetchWithDealFilter('fund-finance-dashboard/tape-kpi', filters);
    return parseKpiRow(body);
  };

  fetchFundFinanceCalledCapitalByDealOverTime = async (filters: FundFinanceDashboardFilters) => {
    const body = await this.fetchWithDealFilter('fund-finance-dashboard/called-capital-by-deal-over-time', filters);
    return parseDealMetricByMonthRows(body);
  };

  fetchFundFinanceUncalledCapitalByDealOverTime = async (filters: FundFinanceDashboardFilters) => {
    const body = await this.fetchWithDealFilter('fund-finance-dashboard/uncalled-capital-by-deal-over-time', filters);
    return parseDealMetricByMonthRows(body);
  };

  fetchFundFinanceSubassetClassOverTime = async (filters: FundFinanceDashboardFilters) => {
    const body = await this.fetchWithDealFilter('fund-finance-dashboard/subasset-class-over-time', filters);
    return parseCategoryMetricByMonthRows(body);
  };

  fetchFundFinanceSponsorParentOverTime = async (filters: FundFinanceDashboardFilters) => {
    const body = await this.fetchWithDealFilter('fund-finance-dashboard/sponsor-parent-over-time', filters);
    return parseSponsorParentOverTime(body);
  };

  fetchFundFinanceSubassetClassByFacility = async (filters: FundFinanceDashboardFilters) => {
    const body = await this.fetchWithDealFilter('fund-finance-dashboard/subasset-class-by-facility', filters);
    return parseDealCategorySliceRows(body);
  };

  fetchFundFinanceGeographyConcentrationOverTime = async (filters: FundFinanceDashboardFilters) => {
    const body = await this.fetchWithDealFilter('fund-finance-dashboard/geography-concentration-over-time', filters);
    return parseGeographyConcentrationOverTime(body);
  };

  fetchFundFinanceWaPercentCalledByDealOverTime = async (filters: FundFinanceDashboardFilters) => {
    const body = await this.fetchWithDealFilter('fund-finance-dashboard/wa-percent-called-by-deal-over-time', filters);
    return parseDealMetricByMonthRows(body);
  };

  fetchFundFinanceCalledVsUncalledOverTime = async (filters: FundFinanceDashboardFilters) => {
    const body = await this.fetchWithDealFilter('fund-finance-dashboard/called-vs-uncalled-over-time', filters);
    return parseCategoryMetricByMonthRows(body);
  };

  fetchFundFinanceCreditQuality = async (filters: FundFinanceDashboardFilters) => {
    const body = await this.fetchWithDealFilter('fund-finance-dashboard/credit-quality', filters);
    return parseCreditQualityRows(body);
  };
}
